import { ToggleGroup, ToggleGroupItem } from "@/components/ui/baseUi/toggle-group"
import { Button } from "@/components/ui/baseUi/button"
import { Text } from "@/components/ui/baseUi/text"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/baseUi/tooltip"
import { cn } from "@/modules/lib/utils"
import { useMySpaceNavigate } from "@/modules/navigation/hooks/useMySpaceNavigate"
import { Path } from "@/modules/navigation/routes"
import { useTemplatePageActions } from "@/modules/template/stores/useTemplatePageStore"
import { useCalendar } from "@modules/calendar/hooks/useCalendar"
import { word } from "@modules/core/utils/i18n"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { TrainingProgramsTab } from "@modules/training/components/trainingProgramsTab"
import dayjs from "dayjs"
import { BookTextIcon, ChevronLeftIcon, ChevronRightIcon, LayoutGridIcon, LayoutListIcon } from "lucide-react"

type ProgramHeaderLeftViewProps = {
  programId: string
  className?: string
} & React.ComponentProps<"div">

export function ProgramHeaderLeftView({ programId, className, ...props }: ProgramHeaderLeftViewProps) {
  const {
    currentWeekTitle,
    prev,
    next,
    selectedDate,
    setSelectedDate,
    isUndated,
    maxDayIndex,
    displayDetailedEvents,
    setDisplayDetailedEvents,
  } = useCalendar()
  const mySpaceNavigate = useMySpaceNavigate()
  const { setPreviousProgramId } = useTemplatePageActions()
  const { sidebarNavigateTo } = useSidebar()
  const canClickPrevButton = isUndated ? typeof selectedDate === "number" && selectedDate >= 7 : true
  const canClickNextButton = isUndated
    ? typeof selectedDate === "number" && maxDayIndex && selectedDate < maxDayIndex - 7
    : true

  const onPrevClicked = () => {
    if (canClickPrevButton) prev()
  }
  const onNextClicked = () => {
    if (canClickNextButton) next()
  }
  const onOpenProgramListFilter = () => {
    void sidebarNavigateTo({
      content: <TrainingProgramsTab allowProgramSelection={false} />,
      replaceAllComponents: true,
    })
  }

  return (
    <div className={cn("flex min-w-100 flex-col gap-4", className)} {...props}>
      <div className="flex h-39 flex-col gap-4 rounded-3xl bg-surface-background-secondary p-4">
        <div className="flex flex-col items-center justify-center gap-2 text-textOnSurface-muted">
          <BookTextIcon color="currentColor" width={32} height={32} />
          <Text fontStyle="TM">{word("global.library.plural", { count: 1 })}</Text>
        </div>
        <div className="flex flex-row gap-2">
          <Button variant="primary" size="md" onClick={onOpenProgramListFilter} className="grow">
            {word("planning.programs")}
          </Button>
          <Button
            variant="secondarySecondary"
            size="md"
            onClick={() => {
              setPreviousProgramId(programId)
              mySpaceNavigate(Path.MySpace.Templates, {
                state: {
                  programId,
                },
              })
            }}
            className="grow"
          >
            {word("global.template.title")}
          </Button>
        </div>
      </div>
      <div
        className={cn(
          "flex h-10.5 flex-row items-center rounded-3xl bg-surface-background-secondary pr-1 pl-2",
          !isUndated && "justify-between",
        )}
      >
        {!isUndated && (
          <Button
            variant="secondarySecondary"
            size="xs"
            onClick={() => {
              setSelectedDate(dayjs())
            }}
            disabled={dayjs().isSame(selectedDate, "day")}
            className="capitalize"
          >
            {word("global.today.label")}
          </Button>
        )}
        <div className={cn("flex flex-row items-center gap-1", isUndated && "ml-8 flex-1 justify-center")}>
          <Button variant="ghost" size="iconMd" onClick={onPrevClicked} disabled={!canClickPrevButton}>
            <ChevronLeftIcon size={24} color="currentColor" />
          </Button>
          <Text fontStyle="BS" className="min-w-28 text-center font-bold">
            {currentWeekTitle}
          </Text>
          <Button variant="ghost" size="iconMd" onClick={onNextClicked} disabled={!canClickNextButton}>
            <ChevronRightIcon size={24} color="currentColor" />
          </Button>
        </div>
        <ToggleGroup variant="emphasisLow" size="icon">
          <ToggleGroupItem
            onClick={() => {
              setDisplayDetailedEvents(false)
            }}
            aria-pressed={!displayDetailedEvents}
          >
            <Tooltip>
              <TooltipTrigger>
                <LayoutGridIcon color="currentColor" size={24} />
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <Text fontStyle="BL">{word("planning.calendar.toggleGroup.tooltip.grid")}</Text>
              </TooltipContent>
            </Tooltip>
          </ToggleGroupItem>
          <ToggleGroupItem
            onClick={() => {
              setDisplayDetailedEvents(true)
            }}
            aria-pressed={displayDetailedEvents}
          >
            <Tooltip>
              <TooltipTrigger>
                <LayoutListIcon color="currentColor" size={24} />
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <Text fontStyle="BL">{word("planning.calendar.toggleGroup.tooltip.list")}</Text>
              </TooltipContent>
            </Tooltip>
          </ToggleGroupItem>
        </ToggleGroup>
      </div>
    </div>
  )
}
